import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import B2BOrderPromotion from "../components/B2BOrderPromotion";
import B2BPaymentSelector from "../components/B2BPaymentSelector";

const euro = (value) => {
  const n = Number(value);
  return Number.isFinite(n)
    ? n.toLocaleString("it-IT", { style: "currency", currency: "EUR" })
    : "Importo non disponibile";
};

const fattFields = [
  ["fatt_ragione_sociale", "Ragione sociale", true],
  ["fatt_piva", "Partita IVA", true],
  ["fatt_cf", "Codice fiscale", false],
  ["fatt_indirizzo", "Indirizzo", true],
  ["fatt_cap", "CAP", true],
  ["fatt_citta", "Città", true],
  ["fatt_provincia", "Provincia", true],
  ["fatt_nazione", "Nazione", true],
  ["fatt_email", "Email", true],
  ["fatt_pec", "PEC", false],
  ["fatt_sdi", "Codice SDI", false],
];

const spedFields = [
  ["sped_destinatario", "Destinatario", true],
  ["sped_indirizzo", "Indirizzo", true],
  ["sped_cap", "CAP", true],
  ["sped_citta", "Città", true],
  ["sped_provincia", "Provincia", true],
  ["sped_nazione", "Nazione", true],
  ["sped_telefono", "Telefono", true],
  ["sped_note", "Note consegna", false],
];

const allFields = [...fattFields, ...spedFields];
const requiredFields = allFields.filter(([, , required]) => required);

function initialForm(ordine) {
  const data = {
    ...(ordine.fatturazione || {}),
    ...(ordine.spedizione || {}),
  };
  return Object.fromEntries(
    allFields.map(([key]) => [key, String(data[key] ?? "")])
  );
}

function FirmaCanvas({ canvasRef, onDraw }) {
  const drawing = useRef(false);

  const point = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * canvas.width / rect.width,
      y: (event.clientY - rect.top) * canvas.height / rect.height,
    };
  };

  const start = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(event.pointerId);
    const ctx = canvas.getContext("2d");
    const p = point(event);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
    drawing.current = true;
    onDraw();
  };

  const move = (event) => {
    if (!drawing.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = point(event);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    onDraw();
  };

  const stop = () => {
    drawing.current = false;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#111111";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [canvasRef]);

  return (
    <canvas
      ref={canvasRef}
      width={760}
      height={250}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={stop}
      onPointerCancel={stop}
      onLostPointerCapture={stop}
      style={{
        width: "100%",
        height: 190,
        border: "1px solid #adb5bd",
        borderRadius: 12,
        background: "#fff",
        touchAction: "none",
        cursor: "crosshair",
      }}
      aria-label="Area per la firma del cliente"
    />
  );
}

export default function AppAgenteOrdineRiepilogoPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const canvasRef = useRef(null);

  const [ordine, setOrdine] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [firmaDisegnata, setFirmaDisegnata] = useState(false);
  const [firmatario, setFirmatario] = useState("");
  const [consenso, setConsenso] = useState(false);
  const [dirty, setDirty] = useState(false);

  const carica = async () => {
    const response = await authFetch(`/api/ordini/${encodeURIComponent(id)}/`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.detail || "Impossibile caricare l'ordine.");
    }
    setOrdine(data);
    setForm(initialForm(data));
    setDirty(false);
    return data;
  };

  useEffect(() => {
    let active = true;
    if (!id || !/^[0-9]+$/.test(String(id))) {
      setError("Numero ordine non valido.");
      setLoading(false);
      return;
    }
    async function avvia() {
      try {
        const response = await authFetch(`/api/ordini/${encodeURIComponent(id)}/`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.detail || "Impossibile caricare l'ordine.");
        if (active) {
          setOrdine(data);
          setForm(initialForm(data));
        }
      } catch (err) {
        if (active) setError(err.message || "Errore caricamento ordine.");
      } finally {
        if (active) setLoading(false);
      }
    }
    avvia();
    return () => { active = false; };
  }, [id]);

  const firmato = Boolean(ordine?.b2b_signed_at);
  const bozza = ordine?.status === "DRAFT";
  const modificabile = bozza && !firmato;
  const prodotti = Array.isArray(ordine?.items) ? ordine.items : [];
  const mancanti = requiredFields
    .filter(([key]) => !String(form[key] ?? "").trim())
    .map(([, label]) => label);
  if (!String(form.fatt_pec ?? "").trim() && !String(form.fatt_sdi ?? "").trim()) {
    mancanti.push("PEC oppure codice SDI");
  }

  const update = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setDirty(true);
    setSuccess("");
  };

  const salva = async () => {
    if (!modificabile) return false;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const response = await authFetch(`/api/ordini/${encodeURIComponent(id)}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || JSON.stringify(data) || "Salvataggio non riuscito.");
      }
      await carica();
      setSuccess("Dati aziendali salvati correttamente.");
      return true;
    } catch (err) {
      setError(err.message || "Errore salvataggio.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const firma = async () => {
    if (!modificabile || busy) return;

    // Pagamento B2B obbligatorio prima della firma.
    if (!["ONLINE", "CONSEGNA"].includes(ordine?.payment_method)) {
      setError("Seleziona e salva il metodo di pagamento prima della firma.");
      return;
    }
    if (mancanti.length) {
      setError("Completa i campi obbligatori: " + mancanti.join(", "));
      return;
    }
    if (dirty) {
      setError("Prima di firmare, salva i dati aziendali.");
      return;
    }
    if (!prodotti.length) {
      setError("L'ordine non contiene prodotti.");
      return;
    }
    if (!firmatario.trim() || !consenso || !firmaDisegnata) {
      setError("Inserisci il nome del firmatario, raccogli il consenso e disegna la firma.");
      return;
    }
    if (!window.confirm("Confermi la registrazione della firma? Dopo la firma l'ordine non sarà più modificabile.")) return;

    setBusy(true);
    setError("");
    setSuccess("");
    try {
      // Una sola acquisizione GPS al momento della firma.
      // Nessun tracciamento continuo. La firma resta possibile
      // anche se il dispositivo non fornisce la posizione.
      const gps = await new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve({ status: "UNAVAILABLE" });
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              status: "AVAILABLE",
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy_m: position.coords.accuracy,
            });
          },
          () => resolve({ status: "UNAVAILABLE" }),
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );
      });

      const signature_png = canvasRef.current.toDataURL("image/png");
      const response = await authFetch(`/api/ordini/${encodeURIComponent(id)}/firma/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          consent: true,
          signer_name: firmatario.trim(),
          signature_png,
          gps,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || JSON.stringify(data) || "Firma non registrata.");
      }
      await carica();
      setSuccess("Firma registrata. Ora puoi inviare l'ordine alla SEDE.");
    } catch (err) {
      setError(err.message || "Errore registrazione firma.");
    } finally {
      setBusy(false);
    }
  };

  const invia = async () => {
    if (!bozza || !firmato || busy) return;
    if (!["ONLINE", "CONSEGNA"].includes(ordine?.payment_method)) {
      setError("Seleziona e salva il metodo di pagamento prima della firma.");
      return;
    }
    if (!window.confirm(
      ordine.payment_method === "ONLINE"
        ? "Procedere al pagamento online sicuro con Stripe?"
        : "Inviare definitivamente l'ordine alla SEDE?"
    )) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      if (ordine.payment_method === "ONLINE") {
        const response = await authFetch(
          `/api/ordini/${encodeURIComponent(id)}/b2b-stripe/checkout/`,
          { method: "POST" }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data?.detail || "Impossibile avviare Stripe.");
        }

        if (
          typeof data.url !== "string" ||
          !/^https:\/\/checkout\.stripe\.com\//i.test(data.url)
        ) {
          throw new Error("URL Stripe Checkout non valido.");
        }

        window.location.assign(data.url);
        return;
      }

      const response = await authFetch(`/api/ordini/${encodeURIComponent(id)}/invia/`, {
        method: "POST",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const missing = Array.isArray(data?.missing) ? ` Campi mancanti: ${data.missing.join(", ")}` : "";
        throw new Error((data?.detail || "Invio non riuscito.") + missing);
      }
      await carica();
      setSuccess("Ordine inviato correttamente alla SEDE.");
    } catch (err) {
      setError(err.message || "Errore invio ordine.");
    } finally {
      setBusy(false);
    }
  };

  const renderFields = (fields) => (
    <div className="row g-3">
      {fields.map(([key, label, required]) => (
        <div className="col-12 col-md-6" key={key}>
          <label className="form-label fw-semibold" htmlFor={key}>
            {label}{required ? " *" : ""}
          </label>
          <input
            id={key}
            className="form-control"
            value={form[key] ?? ""}
            disabled={!modificabile || busy}
            onChange={(event) => update(key, event.target.value)}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="container py-4" style={{ maxWidth: 760 }}>
      <button type="button" className="btn btn-outline-secondary rounded-pill mb-3"
        onClick={() => navigate("/app/agente")}>
        Torna alla home agente
      </button>

      <h2 className="fw-bold mb-3">Riepilogo ordine aziendale</h2>
      {loading && <p>Caricamento ordine...</p>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {success && <div className="alert alert-success" role="status">{success}</div>}

      {!loading && ordine && (
        <>
          <div className="card rounded-4 shadow-sm mb-3">
            <div className="card-body">
              <div className="text-muted small">Ordine n. {ordine.id}</div>
              <h4 className="fw-bold mb-2">{ordine.cliente || ordine.per_nome || "Cliente aziendale"}</h4>
              <div>Stato: <strong>{ordine.status_it || ordine.status}</strong></div>
              {firmato && (
                <div className="text-success mt-2">
                  Firmato da <strong>{ordine.b2b_signer_name || "Cliente"}</strong>
                </div>
              )}
            </div>
          </div>

          <div className="card rounded-4 shadow-sm mb-3">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Prodotti ordinati</h5>
              {!prodotti.length ? (
                <div className="alert alert-warning">Nessun prodotto presente. Non procedere con la firma.</div>
              ) : prodotti.map((riga, index) => (
                <div key={index} className="border-bottom py-3 d-flex justify-content-between gap-3">
                  <div>
                    <div className="fw-semibold">{riga.product_name || `Prodotto ${riga.product || ""}`}</div>
                    <div className="text-muted small">
                      Quantità: {riga.quantity} × {euro(riga.unit_price)}
                    </div>
                  </div>
                  <strong className="text-nowrap">{euro(riga.line_subtotal)}</strong>
                </div>
              ))}
              <div className="d-flex justify-content-between mt-3">
                <span>Totale prodotti</span><strong>{euro(ordine.subtotal)}</strong>
              </div>
              <div className="d-flex justify-content-between mt-2 fs-5">
                <strong>Totale ordine</strong><strong>{euro(ordine.total)}</strong>
              </div>
            </div>
          </div>

          <div className="card rounded-4 shadow-sm mb-3">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Dati di fatturazione</h5>
              {renderFields(fattFields)}
              <div className="small text-muted mt-3">Compila almeno uno tra PEC e codice SDI.</div>
            </div>
          </div>

          <div className="card rounded-4 shadow-sm mb-3">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Dati di spedizione</h5>
              {renderFields(spedFields)}
            </div>
          </div>

          {modificabile && (
            <div className="card rounded-4 shadow-sm mb-3">
              <div className="card-body">
                {mancanti.length > 0 && (
                  <div className="alert alert-warning">
                    <strong>Campi da completare:</strong> {mancanti.join(", ")}
                  </div>
                )}
                <button type="button" className="btn btn-primary w-100"
                  disabled={busy} onClick={salva}>
                  {busy ? "Attendere..." : "Salva dati aziendali"}
                </button>
              </div>
            </div>
          )}

          {modificabile && (
            <div className="card rounded-4 shadow-sm mb-3">
              <B2BOrderPromotion
                ordine={ordine}
                onUpdated={carica}
                disabled={busy || dirty || !modificabile}
              />

              <B2BPaymentSelector
                ordine={ordine}
                onUpdated={carica}
                disabled={busy || dirty || !modificabile}
              />

              <div className="card-body">
                <h5 className="fw-bold mb-2">Firma del cliente</h5>
                {!["ONLINE", "CONSEGNA"].includes(ordine?.payment_method) && (
                  <div className="alert alert-warning" role="alert">
                    Prima di registrare la firma, seleziona e salva
                    il metodo di pagamento dell'ordine.
                  </div>
                )}
                <p className="text-muted small">
                  Mostra al cliente i prodotti, i prezzi e i dati aziendali prima di raccogliere la firma.
                </p>
                <label className="form-label fw-semibold" htmlFor="firmatario">
                  Nome e cognome del firmatario *
                </label>
                <input id="firmatario" className="form-control mb-3"
                  value={firmatario} disabled={busy}
                  onChange={(event) => setFirmatario(event.target.value)} />
                <FirmaCanvas canvasRef={canvasRef} onDraw={() => setFirmaDisegnata(true)} />
                <button type="button" className="btn btn-outline-secondary btn-sm mt-2"
                  disabled={busy}
                  onClick={() => {
                    const canvas = canvasRef.current;
                    if (!canvas) return;
                    const ctx = canvas.getContext("2d");
                    ctx.fillStyle = "#fff";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.strokeStyle = "#111";
                    setFirmaDisegnata(false);
                  }}>
                  Cancella firma
                </button>
                <div className="form-check mt-3 mb-3">
                  <input className="form-check-input" type="checkbox" id="consensoFirma"
                    checked={consenso} disabled={busy}
                    onChange={(event) => setConsenso(event.target.checked)} />
                  <label className="form-check-label" htmlFor="consensoFirma">
                    Il cliente conferma di aver verificato l'ordine e autorizza
                    la registrazione della propria firma.
                  </label>
                </div>
                <button type="button" className="btn btn-success w-100"
                  disabled={
                    busy ||
                    dirty ||
                    !!mancanti.length ||
                    !firmaDisegnata ||
                    !consenso ||
                    !firmatario.trim() ||
                    !prodotti.length ||
                    !["ONLINE", "CONSEGNA"].includes(ordine?.payment_method)
                  }
                  onClick={firma}>
                  Registra firma cliente
                </button>
              </div>
            </div>
          )}

          {bozza && firmato && (
            <div className="card rounded-4 shadow-sm mb-3">
              <div className="card-body">
                <div className="alert alert-success">
                  Firma registrata. L'ordine non è più modificabile.
                </div>
                <button type="button" className="btn btn-success btn-lg w-100"
                  disabled={
                    busy ||
                    !["ONLINE", "CONSEGNA"].includes(ordine.payment_method)
                  } onClick={invia}>
                  {busy
                    ? "Attendere..."
                    : ordine.payment_method === "ONLINE"
                      ? "Procedi al pagamento Stripe"
                      : ordine.payment_method === "CONSEGNA"
                        ? "Invia ordine alla SEDE"
                        : "Seleziona prima il pagamento"}
                </button>
              </div>
            </div>
          )}

          {!bozza && (
            <div className="alert alert-info">
              Ordine non più in bozza. Stato: <strong>{ordine.status_it || ordine.status}</strong>.
            </div>
          )}
        </>
      )}
    </div>
  );
}
